import {
  CAMERA_3D_FAR,
  CAMERA_3D_FOV,
  CAMERA_3D_NEAR,
  CONTROLS_3D_MIN_DISTANCE,
  CONTROLS_3D_SMOOTH,
  CONTROLS_DOLLY_SPEED,
  MODE_3D_VIEW,
} from '@constants'
import { useCachedCamera3D } from '@hooks/useCachedCamera3D'
import { useCameraControls3D } from '@hooks/useCameraControls3D'
import { CameraControls, PerspectiveCamera } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { useAppStore, useUiPresentationStore } from '@store'
import { AnyID } from '@types'
import consoleOnce from '@utils/consoleOnce'
import { startTransition, useRef, useEffect, useMemo } from 'react'
import isArea from '@utils/sceneObjects/assertions/isArea'
import { applyDrawCameraFit } from '@utils/camera/applyDrawCameraFit'
import {
  cancelLiveDrawCameraFitRetries,
  registerCameraCaptureContext,
} from '@utils/camera/cameraControlsRegistry'
import {
  clearActiveDrawFitCameraUp,
  syncDrawCameraOrbitIfPending,
} from '@utils/camera/drawTopDownCamera'
import { calculateItemsBoundingBox } from '@utils/camera/fitCameraToItems'
import {
  applyCameraViewToControls,
  captureCameraViewFromControls,
  hasCameraViewDrift,
  isValidCameraView,
  scheduleApplyCameraViewToControls,
} from '@utils/camera/cameraView'
import { Vector3 } from 'three'

export function CameraManager3D() {
  const isModalOpen = useAppStore.use.isModalOpen()
  // Corresponde a Atemporal commit 47ee2081: sheet abierto (touch) se trata como modal
  // abierto para que el scroll del panel no pelee con OrbitControls.
  const activeSheet = useUiPresentationStore((s) => s.activeSheet)
  const mode = useAppStore.use.mode()
  const lastView = useAppStore((store) => store.lastView)
  const idDisenos = useAppStore((store) => store.clientInfo.idDisenos)
  const idVersiones = useAppStore((store) => store.clientInfo.idVersiones)
  const { controlsRef } = useCameraControls3D()
  useCachedCamera3D()
  const editCameraBoundingBox = useAppStore.use.editCameraBoundingBox()
  const cameraObjectRef = useRef(null)
  const cameraOBB = useAppStore.use.cameraOBB()
  const sceneBoundingBoxes = useAppStore.use.sceneBoundingBoxes()
  const editWall = useAppStore.use.editWallProperty()
  const editAreaProperty = useAppStore.use.editAreaProperty()
  const items = useAppStore.use.items()
  const areas = useAppStore.use.areas()
  const VERTECHO = useAppStore.use.prefs()?.VERTECHO
  const { raycaster, camera, size, viewport, gl, scene } = useThree()
  const shouldFitCameraToItems = useAppStore.use.shouldFitCameraToItems()
  const clearCameraFitToItems = useAppStore.use.clearCameraFitToItems()
  const cameraViewPreservationDepth = useAppStore.use.cameraViewPreservationDepth()
  const lockedCameraView = useAppStore((s) => s.lockedCameraView)
  const cameraViewRestoreNonce = useAppStore((s) => s.cameraViewRestoreNonce)
  const setLastView = useAppStore.use.setLastView()
  const lastCameraLogRef = useRef<{ position: Vector3; target: Vector3; timestamp: number } | null>(
    null,
  )
  const canPersistCameraViewRef = useRef(false)
  const restoredProjectKeyRef = useRef<string | null>(null)
  const projectKey = `${idDisenos}-${idVersiones}`
  const defaultCameraUp = useMemo(() => new Vector3(0, 1, 0), [])

  const itemDragActive = useModularUiStore((s) => s.itemDragActive)
  // Solo bloquear cámara durante arrastre real (itemDragActive), no por modo colgado.
  // Sheet abierto (touch) cuenta como modal para bloquear la cámara (Atemporal 47ee2081).
  const cameraControlsEnabled = !isModalOpen && !activeSheet && !itemDragActive

  useEffect(() => {
    canPersistCameraViewRef.current = false
    restoredProjectKeyRef.current = null
  }, [projectKey])

  useEffect(() => {
    const controls = controlsRef.current as { enabled?: boolean; stop?: () => void } | null
    if (!controls) return
    controls.enabled = cameraControlsEnabled
    if (!cameraControlsEnabled && typeof controls.stop === 'function') {
      controls.stop()
    }
  }, [cameraControlsEnabled, controlsRef])

  useEffect(() => {
    const controls = controlsRef.current
    if (
      !controls ||
      !camera ||
      !('isPerspectiveCamera' in camera) ||
      !camera.isPerspectiveCamera ||
      !gl ||
      !scene
    ) {
      registerCameraCaptureContext(null)
      return
    }

    registerCameraCaptureContext({
      controls,
      camera,
      gl,
      scene,
    })
    return () => registerCameraCaptureContext(null)
  }, [controlsRef, camera, gl, scene])

  useEffect(() => {
    if (
      mode !== MODE_3D_VIEW ||
      !controlsRef.current ||
      shouldFitCameraToItems ||
      cameraViewPreservationDepth > 0
    ) {
      return
    }

    if (!lastView || !isValidCameraView(lastView)) {
      canPersistCameraViewRef.current = true
      return
    }

    if (restoredProjectKeyRef.current === projectKey) {
      canPersistCameraViewRef.current = true
      return
    }

    const controls = controlsRef.current
    if (!controls) return

    applyCameraViewToControls(controls, lastView, false)
    clearActiveDrawFitCameraUp()
    restoredProjectKeyRef.current = projectKey
    canPersistCameraViewRef.current = true
  }, [mode, lastView, projectKey, controlsRef, shouldFitCameraToItems, cameraViewPreservationDepth])

  useEffect(() => {
    if (!controlsRef.current || !camera || shouldFitCameraToItems) return
    if (cameraViewPreservationDepth > 0) return

    const persistCameraView = () => {
      if (!canPersistCameraViewRef.current) return

      const controls = controlsRef.current
      if (!controls) return

      const capturedView = captureCameraViewFromControls(controls, camera)
      const currentPosition = capturedView.position
      const currentTarget = capturedView.target
      const now = Date.now()

      // Solo loguear si ha pasado al menos 500ms desde el último log o si la posición cambió significativamente
      const lastLog = lastCameraLogRef.current
      if (lastLog) {
        const timeSinceLastLog = now - lastLog.timestamp
        const positionChanged =
          currentPosition.distanceTo(lastLog.position) > 10 ||
          currentTarget.distanceTo(lastLog.target) > 10

        if (timeSinceLastLog < 500 && !positionChanged) {
          return
        }
      }

      lastCameraLogRef.current = {
        position: currentPosition,
        target: currentTarget,
        timestamp: now,
      }

      // LOG de depuración (activar con window.__CAMERA_DEBUG__ = true)
      const cameraDebug =
        typeof window !== 'undefined' &&
        (window as Window & { __CAMERA_DEBUG__?: boolean }).__CAMERA_DEBUG__ === true
      const bbox = calculateItemsBoundingBox(useAppStore.getState().items)
      const r = (n: number) => Math.round(n * 100) / 100
      if (cameraDebug) {
        if (bbox) {
          const { center } = bbox
          // eslint-disable-next-line no-console
          // console.log('[CAMERA] posición actual → pega estos valores en constants.ts', {
          //   DRAW_FIT_offsets: {
          //     DRAW_FIT_FIRST_UNIT_CAMERA_OFFSET_X: r(currentPosition.x - center.x),
          //     DRAW_FIT_FIRST_UNIT_CAMERA_Y_OFFSET: r(currentPosition.y - center.y),
          //     DRAW_FIT_FIRST_UNIT_CAMERA_Z_DEPTH: r(center.z - currentPosition.z),
          //     DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_X: r(currentTarget.x - center.x),
          //     DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_Y: r(currentTarget.y - center.y),
          //     DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_Z: r(currentTarget.z - center.z),
          //   },
          //   center: { x: r(center.x), y: r(center.y), z: r(center.z) },
          //   position: { x: r(currentPosition.x), y: r(currentPosition.y), z: r(currentPosition.z) },
          //   target: { x: r(currentTarget.x), y: r(currentTarget.y), z: r(currentTarget.z) },
          // })
        } else {
          // eslint-disable-next-line no-console
          // console.log('[CAMERA] posición actual (sin unidades para calcular centro)', {
          //   position: { x: r(currentPosition.x), y: r(currentPosition.y), z: r(currentPosition.z) },
          //   target: { x: r(currentTarget.x), y: r(currentTarget.y), z: r(currentTarget.z) },
          // })
        }
      }

      setLastView({
        position: currentPosition,
        target: currentTarget,
      })
    }

    const controls = controlsRef.current
    const handleControlStart = () => {
      if (camera && 'isPerspectiveCamera' in camera && camera.isPerspectiveCamera) {
        syncDrawCameraOrbitIfPending(controls, camera)
      }
      cancelLiveDrawCameraFitRetries()
    }
    const handleControlEnd = () => {
      persistCameraView()
    }

    controls?.addEventListener('controlstart', handleControlStart)
    controls?.addEventListener('controlend', handleControlEnd)
    // 'rest' se dispara cuando la cámara se detiene tras CUALQUIER movimiento,
    // incluido el zoom con rueda (que no siempre emite 'controlend').
    controls?.addEventListener('rest', handleControlEnd)

    return () => {
      controls?.removeEventListener('controlstart', handleControlStart)
      controls?.removeEventListener('controlend', handleControlEnd)
      controls?.removeEventListener('rest', handleControlEnd)
    }
  }, [controlsRef, camera, shouldFitCameraToItems, setLastView, cameraViewPreservationDepth])

  useEffect(() => {
    if (!controlsRef.current || !camera || cameraViewRestoreNonce === 0) return

    const controls = controlsRef.current
    if (!controls || !lastView || !isValidCameraView(lastView)) return

    const view = {
      position: lastView.position.clone(),
      target: lastView.target.clone(),
    }

    return scheduleApplyCameraViewToControls(controls, view)
  }, [cameraViewRestoreNonce, controlsRef, camera, lastView])

  // Ajustar la cámara para enfocar unidades solo cuando se solicita explícitamente
  // (no reaccionar a cada cambio de `items`, p. ej. sustitución de unidad).
  useEffect(() => {
    if (
      !shouldFitCameraToItems ||
      !controlsRef.current ||
      isModalOpen ||
      activeSheet ||
      cameraViewPreservationDepth > 0
    ) {
      return
    }

    const controls = controlsRef.current
    if (!controls || !camera) return

    const fitFrame = window.requestAnimationFrame(() => {
      const liveState = useAppStore.getState()
      if (liveState.unitReplaceInFlight || liveState.cameraViewPreservationDepth > 0) {
        clearCameraFitToItems()
        return
      }

      const fittedView = applyDrawCameraFit(controls, camera, liveState.items)
      if (!fittedView) {
        clearCameraFitToItems()
        return
      }

      setLastView({
        position: fittedView.position.clone(),
        target: fittedView.target.clone(),
      })
      clearCameraFitToItems()
    })

    return () => {
      window.cancelAnimationFrame(fitFrame)
    }
  }, [
    shouldFitCameraToItems,
    controlsRef,
    camera,
    isModalOpen,
    activeSheet,
    clearCameraFitToItems,
    cameraViewPreservationDepth,
    setLastView,
  ])

  useFrame(() => {
    if (cameraViewPreservationDepth > 0 && lockedCameraView && controlsRef.current && camera) {
      const controls = controlsRef.current
      const currentView = captureCameraViewFromControls(controls, camera)
      if (hasCameraViewDrift(currentView, lockedCameraView)) {
        applyCameraViewToControls(controls, lockedCameraView, false)
      }
    }
  })

  const wallOcclusionFrameRef = useRef(0)
  const wallVisibleCacheRef = useRef<Map<string, boolean>>(new Map())
  const ceilingVisibleCacheRef = useRef<Map<string, boolean>>(new Map())
  const wallYResetDoneRef = useRef<Set<string>>(new Set())

  useFrame(() => {
    if (
      !cameraObjectRef.current ||
      !cameraOBB ||
      !raycaster ||
      !raycaster.camera ||
      !raycaster.camera.position ||
      !raycaster.camera.rotation ||
      location.href.includes('openProject') ||
      location.href.includes('downloading')
    ) {
      return
    }

    consoleOnce('looking for camera position', {
      pos: raycaster.camera.position,
      rot: raycaster.camera.rotation,
    })

    editCameraBoundingBox(raycaster.camera.position, raycaster.camera.rotation)

    // OBB wall/ceiling culling: throttle + only write store when visibility actually changes
    wallOcclusionFrameRef.current += 1
    if (wallOcclusionFrameRef.current % 3 === 0) {
      for (const [id, itemOBB] of sceneBoundingBoxes.entries()) {
        const _id = id.replace('COBB_', '') as AnyID
        const wallInfo = items.get(_id)
        if (!wallInfo) continue
        const intersects = cameraOBB.intersectsOBB(itemOBB.boundingBox)
        const nextWallVisible = !intersects
        const prevWallVisible = wallVisibleCacheRef.current.get(_id)

        if (prevWallVisible !== nextWallVisible) {
          wallVisibleCacheRef.current.set(_id, nextWallVisible)
          startTransition(() => {
            editWall(_id, { wallVisible: nextWallVisible })
          })
        }

        if (!wallInfo.visible && !wallYResetDoneRef.current.has(_id)) {
          wallYResetDoneRef.current.add(_id)
          startTransition(() => {
            editWall(_id, { y: 0 })
          })
        } else if (wallInfo.visible) {
          wallYResetDoneRef.current.delete(_id)
        }
      }

      if (VERTECHO) {
        for (const [id, itemOBB] of sceneBoundingBoxes.entries()) {
          if (!id.startsWith('COBB_CEILING_')) continue
          const areaId = id.replace('COBB_CEILING_', '')
          const areaInfo = areas.get(areaId)
          if (!areaInfo || !isArea(areaInfo) || !areaInfo.isRoof) continue

          const intersects = cameraOBB.intersectsOBB(itemOBB.boundingBox)
          const nextCeilingVisible = !intersects
          const prevCeilingVisible = ceilingVisibleCacheRef.current.get(areaId)

          if (prevCeilingVisible !== nextCeilingVisible) {
            ceilingVisibleCacheRef.current.set(areaId, nextCeilingVisible)
            startTransition(() => {
              editAreaProperty(areaId, { ceilingVisible: nextCeilingVisible })
            })
          }
        }
      }
    }
  })
  return (
    <group>
      <PerspectiveCamera
        makeDefault
        frustumCulled={false}
        ref={cameraObjectRef}
        up={defaultCameraUp}
        near={CAMERA_3D_NEAR}
        far={CAMERA_3D_FAR}
        fov={CAMERA_3D_FOV}
      />
      <CameraControls
        makeDefault
        ref={controlsRef}
        infinityDolly={true}
        smoothTime={CONTROLS_3D_SMOOTH}
        dollySpeed={CONTROLS_DOLLY_SPEED}
        minDistance={CONTROLS_3D_MIN_DISTANCE}
        enabled={cameraControlsEnabled}
      />
    </group>
  )
}
